import { PageHeader } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Mail, Phone, Clock, ChevronDown, ChevronRight, ChevronLeft, Search } from "lucide-react";
import { useLanguage } from "@/features/language/contexts/LanguageContext";
import { helpAPI } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";

interface FAQ {
	id: string;
	question: string;
	answer: string;
	category: string;
	tags: string[];
}

interface HelpCategory {
	id: string;
	title: string;
	description: string;
	icon: React.ReactNode;
	articles: number;
}

const HelpPage: React.FC = () => {
	const { t } = useTranslation();
	const { isRTL, language } = useLanguage();
	const { toast } = useToast();

	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCategory, setSelectedCategory] = useState<string>("");
	const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
	const [helpCategories, setHelpCategories] = useState<HelpCategory[]>([]);
	const [faqs, setFaqs] = useState<FAQ[]>([]);
	const [faqTitle, setFaqTitle] = useState<string>("");
	const [loading, setLoading] = useState(true);
	const [contactInfo, setContactInfo] = useState<any>(null);

	const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

	useEffect(() => {
		fetchHelpData();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when language changes
	}, [language]);

	const fetchHelpData = async () => {
		try {
			setLoading(true);
			const [categoriesData, faqsData, contactData] = await Promise.all([
				helpAPI.getHelpContent(language),
				helpAPI.getFaqs(language),
				helpAPI.getContactInfo(language),
			]);

			// Map categories
			const mappedCategories: HelpCategory[] = categoriesData.map((cat: any) => ({
				id: cat.id,
				title: cat.title,
				description: cat.description,
				articles: cat.articles,
			}));

			setHelpCategories(mappedCategories);

			// Map FAQs - handle both { faqs: [...] } and direct array response
			const faqsArray = Array.isArray(faqsData) ? faqsData : faqsData?.faqs || [];
			const mappedFaqs: FAQ[] = faqsArray.map((faq: any, index: number) => ({
				id: faq.id || `faq-${index}`,
				question: faq.question || "",
				answer: faq.answer || "",
				category: faq.category || "",
				tags: faq.tags || [],
			}));

			// Store FAQ title from API if available
			setFaqTitle(faqsData?.title || t("help.faqs.title"));
			setFaqs(mappedFaqs);
			setContactInfo(contactData);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("help.errors.fetchFailed"),
				variant: "destructive",
			});
			// Use default data on error
			setFaqTitle(t("help.faqs.title"));
		} finally {
			setLoading(false);
		}
	};

	const filteredFAQs = faqs.filter(faq => {
		const matchesSearch =
			faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
			faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
		const matchesCategory = !selectedCategory || faq.category === selectedCategory;
		return matchesSearch && matchesCategory;
	});

	const toggleFAQ = (faqId: string) => {
		setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
	};

	if (loading) return <Loading />;

	return (
		<div className={`space-y-6 p-6 ${isRTL ? "text-right" : "text-left"}`}>
			<PageHeader title={t("navigation.help")} />
			{/* Contact Information */}
			<div className="space-y-6">
				<h2 className="text-xl font-semibold">{t("help.contact.contactInformation")}</h2>
				<div className="grid gap-6 md:grid-cols-3">
					{contactInfo?.email ? (
						<Card className="text-center">
							<CardHeader>
								<div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
									<Mail className="h-6 w-6" />
								</div>
								<CardTitle className="text-base">{t("help.contact.emailLabel")}</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-muted-foreground">
									<a
										href={`mailto:${contactInfo?.email}`}
										className="text-primary hover:text-primary"
									>
										{contactInfo?.email}
									</a>
								</p>
								<p className="text-sm text-muted-foreground mt-2">{t("help.contact.responseTime")}</p>
							</CardContent>
						</Card>
					) : null}

					{contactInfo?.phone ? (
						<Card className="text-center">
							<CardHeader>
								<div className="mx-auto w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center text-success mb-4">
									<Phone className="h-6 w-6" />
								</div>
								<CardTitle className="text-base">{t("help.contact.phoneLabel")}</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-muted-foreground text-center" dir="ltr">
									<a href={`tel:${contactInfo?.phone}`} className="text-primary hover:text-primary">
										{contactInfo?.phone}
									</a>
								</p>
								{contactInfo?.phone2 && (
									<p className="text-muted-foreground mt-2 text-center" dir="ltr">
										<a
											href={`tel:${contactInfo?.phone2}`}
											className="text-primary hover:text-primary"
										>
											{contactInfo?.phone2}
										</a>
									</p>
								)}
							</CardContent>
						</Card>
					) : null}

					{contactInfo?.hours ? (
						<Card className="text-center">
							<CardHeader>
								<div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary mb-4">
									<Clock className="h-6 w-6" />
								</div>
								<CardTitle className="text-base">{t("help.contact.workingHoursLabel")}</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-muted-foreground">{contactInfo?.days}</p>
								<p className="text-sm text-muted-foreground mt-2">{contactInfo?.hours}</p>
							</CardContent>
						</Card>
					) : null}
				</div>
			</div>

			{/* FAQs */}
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<h2 className="text-xl font-semibold">{faqTitle || t("help.faqs.title")}</h2>
					<select
						aria-label={t("help.categories.allCategories")}
						value={selectedCategory}
						onChange={e => setSelectedCategory(e.target.value)}
						className="flex h-12 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 w-full max-w-96"
					>
						<option value="">{t("help.categories.allCategories")}</option>
						{helpCategories.map(category => (
							<option key={category.id} value={category.id}>
								{category.title}
							</option>
						))}
					</select>
				</div>

				{/* Search */}
				<Card>
					<CardContent className="pt-6">
						<div className="relative">
							<Search
								className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground`}
							/>
							<Input
								placeholder={t("help.searchPlaceholder")}
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								className={isRTL ? "pr-10" : "pl-10"}
							/>
						</div>
					</CardContent>
				</Card>

				<div className="space-y-4">
					{filteredFAQs.length === 0 && (selectedCategory !== "" || searchTerm !== "") && (
						<div className="text-center text-muted-foreground">{t("common.noResults")}</div>
					)}
					{filteredFAQs.map(faq => (
						<Card key={faq.id}>
							<CardHeader
								className="cursor-pointer hover:bg-muted/50 transition-colors"
								onClick={() => toggleFAQ(faq.id)}
							>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<CardTitle className="text-base">{faq.question}</CardTitle>
										<div className="flex gap-2">
											{faq.tags.map(tag => (
												<Badge key={tag} variant="outline" className="text-xs">
													{tag}
												</Badge>
											))}
										</div>
									</div>
									{expandedFAQ === faq.id ? (
										<ChevronDown className="h-5 w-5 text-muted-foreground" />
									) : (
										<ChevronIcon className="h-5 w-5 text-muted-foreground" />
									)}
								</div>
							</CardHeader>
							{expandedFAQ === faq.id && (
								<CardContent>
									<ul className="text-muted-foreground list-decimal list-inside px-2">
										{faq.answer?.split(". ").map((answer, index) => (
											<li key={index}>{answer}</li>
										))}
									</ul>
								</CardContent>
							)}
						</Card>
					))}
				</div>
			</div>
		</div>
	);
};

export default HelpPage;
